import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import CodeInput from '../components/auth/CodeInput';
import TotpSetup from '../components/auth/TotpSetup';

// Sign-in only. Accounts are invite-only (S0-1): a new user is emailed a temporary password,
// they don't self-register. The old "Create Account" tab could only ever 401 and described
// bootstrap behaviour that no longer exists (FIND-10).
//
// A sign-in can take more than the password (#179). The API may answer with a step instead
// of a session — a code from an authenticator app, setting one up (Verlo's staff must have
// one), or choosing a new password in place of an invite's temporary one — and this page
// shows each in the same card until it gets a session.
//
// Styled to read as the same product as the kiosk (bg-surface-2, the Verlo mark front and
// centre, a single calm card) rather than its own dark generic-SaaS look — a manager
// signing in on their laptop should recognise it as Verlo before they read a word.

const MIN_PASSWORD_LENGTH = 10;

// The API's answers that mean the step is gone and the person starts again from the password.
const STEP_GONE = /start again|sign in again/i;

const STEP_TITLES = {
  totp: 'Two-factor code',
  totp_setup: 'Set up two-factor',
  new_password: 'Choose your password',
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, respondToStep } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [remember, setRemember] = useState(false);

  // The step the API asked for, if any, and its answers.
  const [step, setStep] = useState(null);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [stepError, setStepError] = useState('');

  const land = (result) => {
    if (result?.step) {
      setStep(result);
      setCode('');
      setNewPassword('');
      setConfirmPassword('');
      setStepError('');
      return;
    }
    navigate(result?.default_landing_path || '/back-office');
  };

  const startAgain = (message) => {
    setStep(null);
    setLoginPassword('');
    if (message) toast.error(message);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      land(await login(loginEmail, loginPassword, remember));
    } catch (err) {
      // A 403 here is the "account not set up yet" case (routes/auth.py) — its detail
      // already says to ask for the invitation again, so it reads fine as a plain toast.
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStep = async (e) => {
    e.preventDefault();
    setStepError('');
    let answer;
    if (step.step === 'new_password') {
      if (newPassword.length < MIN_PASSWORD_LENGTH) {
        setStepError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
        return;
      }
      if (newPassword !== confirmPassword) {
        setStepError("The two passwords don't match.");
        return;
      }
      answer = { new_password: newPassword };
    } else {
      answer = { code };
    }
    setLoading(true);
    try {
      land(await respondToStep(step.challenge_token, answer));
    } catch (err) {
      if (STEP_GONE.test(err.message)) startAgain(err.message);
      else setStepError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-surface-2 flex flex-col items-center justify-center px-6 py-10"
      data-testid="login-page"
    >
      <div className="w-full max-w-xs mb-8 shrink-0">
        <img
          src="/verlo-logo-blue.svg"
          alt="Verlo"
          className="w-full h-auto max-h-16 object-contain"
          data-testid="login-logo"
        />
      </div>

      <div className="w-full max-w-sm">
        <div className="bg-white border border-hairline rounded-lg  p-6">
          {step ? (
            <form onSubmit={handleStep} className="space-y-4" data-testid={`login-step-${step.step}`}>
              <h1 className="text-lg font-semibold text-ink">{STEP_TITLES[step.step]}</h1>

              {step.step === 'totp' && (
                <div className="space-y-1.5">
                  <Label htmlFor="login-code" className="text-xs text-ink-soft">
                    Enter the code from your authenticator app.
                  </Label>
                  <CodeInput id="login-code" value={code} onChange={setCode} data-testid="login-code" />
                </div>
              )}

              {step.step === 'totp_setup' && (
                <>
                  <p className="text-[13px] text-ink-soft">
                    This account needs two-factor sign-in. You&apos;ll be asked for a code from the
                    app each time you sign in.
                  </p>
                  <TotpSetup setup={step} code={code} onCodeChange={setCode} idPrefix="login-setup" />
                </>
              )}

              {step.step === 'new_password' && (
                <>
                  <p className="text-[13px] text-ink-soft">
                    You signed in with a temporary password. Choose your own to carry on.
                  </p>
                  <div className="space-y-1.5">
                    <Label htmlFor="login-new-password" className="text-xs text-ink-soft">New password</Label>
                    <Input
                      id="login-new-password"
                      type="password"
                      autoComplete="new-password"
                      autoFocus
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      data-testid="login-new-password"
                    />
                    <p className="text-[12px] text-ink-faint">At least {MIN_PASSWORD_LENGTH} characters.</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="login-confirm-password" className="text-xs text-ink-soft">Confirm</Label>
                    <Input
                      id="login-confirm-password"
                      type="password"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      data-testid="login-confirm-password"
                    />
                  </div>
                </>
              )}

              {stepError && (
                <p className="text-[13px] text-signal-stop" data-testid="login-step-error">{stepError}</p>
              )}

              <Button
                type="submit"
                className="w-full bg-verlo hover:bg-verlo-hover"
                disabled={loading || (step.step !== 'new_password' && code.length !== 6)}
                data-testid="login-step-submit"
              >
                {loading ? 'Checking…' : 'Continue'}
              </Button>
              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => startAgain()}
                  className="text-[12px] text-ink-soft hover:text-verlo hover:underline"
                  data-testid="login-step-back"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          ) : (
            <>
              <h1 className="text-lg font-semibold text-ink mb-5">Sign in</h1>

              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="login-email" className="text-xs text-ink-soft">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@restaurant.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                    data-testid="login-email"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="login-password" className="text-xs text-ink-soft">Password</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      data-testid="login-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent text-ink-faint"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide the password' : 'Show the password'}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </Button>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-[12px] text-ink-soft select-none">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-ink-faint text-verlo focus:ring-verlo"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    data-testid="login-remember"
                  />
                  Remember this device for 30 days
                </label>
                <Button
                  type="submit"
                  className="w-full bg-verlo hover:bg-verlo-hover"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? 'Signing in…' : 'Sign In'}
                </Button>
                <div className="text-center pt-1">
                  <Link
                    to="/forgot-password"
                    className="text-[12px] text-ink-soft hover:text-verlo hover:underline"
                    data-testid="login-forgot-link"
                  >
                    Forgot your password?
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
